import { createHash } from "node:crypto";
import { z } from "zod";
import type { ConversationDoc, CorpusConversation, MessageDoc } from "@llmnesia/shared-types";

/**
 * Lenient input schema for the MCP `save_conversation` tool. A calling LLM
 * supplies the essentials (platform, title, messages); everything else is
 * optional and filled with sensible defaults during normalization. This is the
 * zod mirror of ConversationDoc/MessageDoc referenced in PLAN-MCP.md §3.
 */
export const saveMessageSchema = z.object({
  role: z.enum(["user", "assistant", "system", "unknown"]).default("unknown"),
  text: z.string().min(1, "message text must not be empty"),
  formattedText: z.string().optional(),
  msgIndex: z.number().int().optional(),
  createdAt: z.number().optional(),
  platformMessageId: z.string().nullish()
});

export const saveConversationSchema = z.object({
  docId: z.string().optional(),
  platform: z.string().min(1),
  sourceId: z.string().nullish(),
  title: z.string().optional(),
  url: z.string().optional(),
  createdAt: z.number().optional(),
  updatedAt: z.number().optional(),
  preview: z.string().optional(),
  pinned: z.boolean().optional(),
  summary: z
    .object({
      text: z.string(),
      recipe: z.string().default("mcp"),
      method: z.string().default("provided"),
      createdAt: z.number().optional()
    })
    .optional(),
  messages: z.array(saveMessageSchema).min(1, "at least one message is required")
});

export type SaveConversationInput = z.infer<typeof saveConversationSchema>;

const PREVIEW_MAX = 160;

/** Normalize a validated save payload into a complete CorpusConversation. */
export function normalizeSaveInput(input: SaveConversationInput, now = Date.now()): CorpusConversation {
  const docId = resolveDocId(input);
  const createdAt = input.createdAt ?? now;
  const updatedAt = input.updatedAt ?? now;

  const messages: MessageDoc[] = input.messages.map((m, i) => {
    const msgIndex = m.msgIndex ?? i;
    const message: MessageDoc = {
      msgKey: `${docId}:${msgIndex}`,
      docId,
      msgIndex,
      role: m.role,
      text: m.text,
      createdAt: m.createdAt ?? createdAt,
      updatedAt: m.createdAt ?? updatedAt,
      contentHash: sha1(m.text)
    };
    if (m.formattedText) {
      message.formattedText = m.formattedText;
    }
    if (m.platformMessageId !== undefined) {
      message.platformMessageId = m.platformMessageId ?? null;
    }
    return message;
  });
  messages.sort((a, b) => a.msgIndex - b.msgIndex);

  const title = input.title?.trim() || deriveTitle(messages);
  const preview = input.preview?.trim() || derivePreview(messages);
  const estimatedBytes = messages.reduce((sum, m) => sum + Buffer.byteLength(m.text, "utf8"), 0);

  const conversation: ConversationDoc = {
    docId,
    platform: input.platform as ConversationDoc["platform"],
    title,
    url: input.url ?? "",
    createdAt,
    contentUpdatedAt: updatedAt,
    indexedAt: now,
    updatedAt,
    lastSeenAt: now,
    messageCount: messages.length,
    preview,
    hasImages: false,
    pinned: input.pinned ?? false,
    indexLevel: "hot",
    estimatedBytes,
    sourceId: input.sourceId ?? null,
    origin: "import"
  };
  if (input.summary) {
    conversation.summary = {
      text: input.summary.text,
      recipe: input.summary.recipe,
      method: input.summary.method,
      createdAt: input.summary.createdAt ?? now
    };
  }

  return { conversation, messages };
}

// Stable identity: explicit docId wins; else platform:sourceId; else derive a
// content hash so re-saving the same conversation is idempotent by docId.
function resolveDocId(input: SaveConversationInput): string {
  if (input.docId) {
    return input.docId;
  }
  if (input.sourceId) {
    return `${input.platform}:${input.sourceId}`;
  }
  const basis = input.messages.map((m) => `${m.role} ${m.text}`).join("");
  return `${input.platform}:${sha1(basis).slice(0, 16)}`;
}

function deriveTitle(messages: MessageDoc[]): string {
  const firstUser = messages.find((m) => m.role === "user") ?? messages[0];
  const text = firstUser?.text.replace(/\s+/g, " ").trim() ?? "Untitled conversation";
  return text.length > 80 ? `${text.slice(0, 79)}…` : text || "Untitled conversation";
}

function derivePreview(messages: MessageDoc[]): string {
  const firstUser = messages.find((m) => m.role === "user") ?? messages[0];
  const text = firstUser?.text.replace(/\s+/g, " ").trim() ?? "";
  return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX - 1)}…` : text;
}

function sha1(value: string): string {
  return createHash("sha1").update(value).digest("hex");
}
